import React, { useState } from 'react'
import MaskedInput from 'react-text-mask'
import _values from 'lodash/values'
import { useDispatch } from 'react-redux'

import { useTheme } from '@material-ui/styles'
import {
  CircularProgress,
  Dialog,
  DialogContent,
  TextField,
  DialogTitle,
  DialogActions,
  Typography,
  IconButton,
  Button,
  makeStyles
} from '@material-ui/core'
import { Close } from '@material-ui/icons'

import { useLiterals } from '~/components/Literals'

import useViewportSize from '~/components/useViewportSize'
import api from '~/api'
import actions from '~/actions'
import track from '~/track'

const useStyles = makeStyles({
  rootBackdrop: { position: 'absolute' }
})

function validateCnpj(cnpj) {
  const fakes = [
    '00000000000000',
    '11111111111111',
    '22222222222222',
    '33333333333333',
    '44444444444444',
    '55555555555555',
    '66666666666666',
    '77777777777777',
    '88888888888888',
    '99999999999999'
  ]
  if (cnpj == '') return false

  if (cnpj.length != 14) return false

  // Elimina CNPJs invalidos conhecidos
  if (fakes.includes(cnpj)) return false

  // Valida DVs
  let tamanho = cnpj.length - 2
  let numeros = cnpj.substring(0, tamanho)
  let digitos = cnpj.substring(tamanho)
  let soma = 0
  let pos = tamanho - 7
  for (let i = tamanho; i >= 1; i--) {
    soma += numeros.charAt(tamanho - i) * pos--
    if (pos < 2) pos = 9
  }
  let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11)
  if (resultado != digitos.charAt(0)) return false

  tamanho = tamanho + 1
  numeros = cnpj.substring(0, tamanho)
  soma = 0
  pos = tamanho - 7
  for (let i = tamanho; i >= 1; i--) {
    soma += numeros.charAt(tamanho - i) * pos--
    if (pos < 2) pos = 9
  }
  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11)
  if (resultado != digitos.charAt(1)) return false

  return true
}

function validate(values, getLiteral) {
  const errors = {}

  if (!values.username) {
    errors.username = getLiteral('2042')
  }

  if (!values.name) {
    errors.name = getLiteral('2043')
  }

  if (!values.email) {
    errors.email = getLiteral('2044')
  } else if (!/^.+@.+\..+$/.test(values.email)) {
    errors.email = getLiteral('1220')
  }

  if (!values.phone) {
    errors.phone = getLiteral('2045')
  }

  if (!values.cnpj.length || (values.cnpj && !validateCnpj(values.cnpj.replace(/[^\d]+/g, '')))) {
    errors.cnpj = getLiteral('2046')
  }

  return errors
}

const cnpjMask = [
  /\d/,
  /\d/,
  '.',
  /\d/,
  /\d/,
  /\d/,
  '.',
  /\d/,
  /\d/,
  /\d/,
  '/',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/
]

const initialState = {
  fields: {
    name: '',
    email: '',
    username: '',
    phone: '',
    cnpj: ''
  },
  errors: {}
}

function RetailForm({ open, setRetailOpen, onDone, position }) {
  const dispatch = useDispatch()
  const theme = useTheme()
  const viewport = useViewportSize()
  const classes = useStyles()

  const [state, setState] = useState(initialState)
  const [loading, setLoading] = useState(false)

  const { getLiteral } = useLiterals()

  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const onChange = ({ target: { name, value } }) => {
    setState((state) => ({
      ...state,
      error: false,
      fields: {
        ...state.fields,
        [name]: value
      },
      errors: {
        ...state.errors,
        [name]: false
      }
    }))
  }

  const throwError = (err) => {
    if (!err.field) {
      dispatch(
        actions.snack.add({
          id: Date.now(),
          message: err.message
        })
      )
    }

    if (err.field === 'cnpj') {
      let errors = { cnpj: err.message }
      setState((s) => ({ ...s, errors }))
    }
  }

  const handleCancel = () => {
    if (!loading) {
      track('queroRevenderCancelar', { name: 'cancelar', category: 'menu' })
      setRetailOpen(false) && setState(initialState)
    }
  }

  const hasEmptyFields = () => {
    const { fields: values } = state

    return _values(values).some((field) => !field || !field.length)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    const { fields: values } = state
    const errors = validate(values, getLiteral)
    const hasErrors = Object.keys(errors).length > 0

    track('queroRevenderEnvioForm', { name: values.email, category: 'menu' })

    if (hasErrors) {
      setState((s) => ({ ...s, errors }))
    } else {
      try {
        await api.retailMessage({ ...values, location: position.getAddress() })
        setLoading(false)

        dispatch(
          actions.snack.add({
            id: Date.now(),
            message: getLiteral('2186')
          })
        )
        onDone()
      } catch (err) {
        throwError(err)
        setLoading(false)
      }
    }
  }

  const getProps = (field) => {
    const error = state.errors[field]
    return {
      name: field,
      error: !!error,
      helperText: error,
      value: state.fields[field],
      id: field,
      fullWidth: true,
      onChange,
      InputLabelProps: { shrink: true },
      variant: 'outlined',
      style: { marginBottom: 16 }
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullScreen={isMobile}
      container={document.getElementById('gofind-finder') || document.body}
      style={{ position: 'absolute' }}
      BackdropProps={{
        classes: { root: classes.rootBackdrop }
      }}
    >
      <DialogTitle
        disableTypography
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="h6">{getLiteral('1870')}</Typography>

        <IconButton aria-label={getLiteral('1251')} size="small" onClick={handleCancel}>
          <Close style={{ color: theme.palette.text.primary }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="h6" gutterBottom>
          {getLiteral('2047')}
        </Typography>
        <br />

        <TextField
          {...getProps('username')}
          label={getLiteral('1067')}
          placeholder={getLiteral('2048')}
        />
        <TextField
          {...getProps('name')}
          label={getLiteral('1563')}
          placeholder={getLiteral('2049')}
        />
        <MaskedInput
          {...getProps('cnpj')}
          mask={cnpjMask}
          placeholder="00.000.000/0000-00"
          label={getLiteral('2050')}
          render={(ref, props) => <TextField InputProps={{ inputRef: ref }} {...props} />}
        />
        <TextField
          {...getProps('email')}
          label={getLiteral('1136')}
          placeholder={getLiteral('2051')}
          type="email"
        />
        <TextField
          {...getProps('phone')}
          label={getLiteral('1032')}
          placeholder="(00) 00000-0000"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading} style={{ textDecoration: 'underline' }}>
          {getLiteral('1039')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || hasEmptyFields()}
        >
          {loading ? <CircularProgress size={20} /> : getLiteral('1744')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RetailForm
